// ✨ REAL-TIME 2025-06-03 - Auto-refreshes badge when new requests arrive
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './NotificationDropdown.css';          // <-- keep CSS next to the component
import { FaBell } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL;

const NotificationDropdown = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [friendRequests, setFriendRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(new Set());
  const dropdownRef = useRef(null);

  /* ---------- helpers ---------- */
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const formatTimeAgo = dateString => {
    const now  = new Date();
    const date = new Date(dateString);
    const diff = Math.floor((now - date) / 60000);           // minutes
    if (diff < 1)  return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    const h = Math.floor(diff / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  /* ---------- data fetching ---------- */
  const fetchFriendRequests = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      
      const { data } = await axios.get(`${API_URL}/friends/requests`, {
        headers: getAuthHeaders()
      });
      
      console.log('[Notifications] Fetched requests:', data);
      setFriendRequests(data.requests || []);
      
    } catch (err) {
      console.error('[Notifications] load error:', err);
      setFriendRequests([]);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  /* ---------- friend-request actions ---------- */
  const respondToRequest = async (id, action) => {
    if (processing.has(id)) return;
    setProcessing(prev => new Set(prev).add(id));

    try {
      // Fix: Convert 'accepted'/'declined' to 'accept'/'decline' for backend
      const backendAction = action === 'accepted' ? 'accept' : 'decline';
      
      console.log(`[Notifications] Responding to request ${id} with action: ${backendAction}`);
      
      const response = await axios.put(
        `${API_URL}/friends/respond/${id}`,
        { action: backendAction },
        { headers: getAuthHeaders() }
      );

      console.log(`[Notifications] Response received:`, response.data);

      // Update the local state immediately to show status
      setFriendRequests(prev =>
        prev.map(r =>
          r.request_id === id 
            ? { 
                ...r, 
                status: action === 'accepted' ? 'accepted' : 'declined',
                responded_at: new Date()
              } 
            : r
        )
      );

      console.log(`[Notifications] Successfully ${action} friend request ${id}`);
      
    } catch (err) {
      console.error(`[Notifications] ${action} error:`, err);
      
      // More detailed error messaging
      const errorMsg = err.response?.data?.error || err.message || 'Unknown error occurred';
      alert(`Failed to ${action === 'accepted' ? 'accept' : 'decline'} request: ${errorMsg}`);
      
    } finally {
      setProcessing(prev => {
        const set = new Set(prev);
        set.delete(id);
        return set;
      });
    }
  };

  /* ---------- dropdown open / close logic ---------- */
  useEffect(() => {
    const clickOutside = e => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        !e.target.closest('.notification-icon')
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', clickOutside);
    return () => document.removeEventListener('mousedown', clickOutside);
  }, []);

  // Fetch requests when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchFriendRequests();
    }
  }, [isOpen, fetchFriendRequests]);

  // REAL-TIME UPDATES: Check for new requests periodically
  useEffect(() => {
    // Initial fetch
    fetchFriendRequests(false);

    // Set up polling every 30 seconds for real-time updates
    const interval = setInterval(() => {
      fetchFriendRequests(false);
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchFriendRequests]);

  // Listen for custom events when friend requests are sent/received
  useEffect(() => {
    const handleFriendRequestUpdate = (event) => {
      console.log('[Notifications] Friend request update event received:', event.type, event.detail);
      fetchFriendRequests(false);
    };

    // Listen for all types of friend request events
    window.addEventListener('friendRequestSent', handleFriendRequestUpdate);
    window.addEventListener('friendRequestReceived', handleFriendRequestUpdate);
    window.addEventListener('friendRequestResponded', handleFriendRequestUpdate);
    window.addEventListener('notificationRefresh', handleFriendRequestUpdate);

    return () => {
      window.removeEventListener('friendRequestSent', handleFriendRequestUpdate);
      window.removeEventListener('friendRequestReceived', handleFriendRequestUpdate);
      window.removeEventListener('friendRequestResponded', handleFriendRequestUpdate);
      window.removeEventListener('notificationRefresh', handleFriendRequestUpdate);
    };
  }, [fetchFriendRequests]);

  // Listen for page visibility changes to refresh when user returns
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('[Notifications] Page became visible, refreshing requests');
        fetchFriendRequests(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchFriendRequests]);

  /* ---------- render ---------- */
  // Only count pending requests for badge
  const pending = friendRequests.filter(r => r.status === 'pending');
  const badge = pending.length;

  console.log('[Notifications] Render state:', {
    totalRequests: friendRequests.length,
    pendingCount: badge,
    isOpen,
    requests: friendRequests.map(r => ({ id: r.request_id, status: r.status, name: r.name }))
  });

  return (
    <div className="notification-container">
      {/* bell icon */}
      <div
        className="notification-icon"
        title="Notifications"
        onClick={() => setIsOpen(!isOpen)}
      >
        <FaBell size={18} />    {/* FA v6 "solid" style */}
        {badge > 0 && <span className="notification-badge">{badge}</span>}
      </div>

      {isOpen && (
        <div className="notification-dropdown" ref={dropdownRef}>
          <div className="notification-header">
            <h3>Notifications</h3>
            {loading && <div className="notification-loading">Loading…</div>}
            {!loading && (
              <button 
                className="refresh-btn"
                onClick={() => fetchFriendRequests()}
                title="Refresh notifications"
              >
                🔄
              </button>
            )}
          </div>

          <div className="notification-content">
            {friendRequests.length ? (
              friendRequests.map(req => {
                // Check if this is a duplicate user (same user has multiple requests)
                const sameUserRequests = friendRequests.filter(r => r.sender_id === req.sender_id);
                const hasPendingRequest = sameUserRequests.some(r => r.status === 'pending');
                const isOldDeclined = req.status === 'declined' && hasPendingRequest;

                return (
                  <div 
                    key={req.request_id} 
                    className={`notification-item friend-request ${isOldDeclined ? 'old-declined' : ''}`}
                  >
                    {/* --- avatar column --- */}
                    <div className="avatar-column">
                      <img
                        src={req.avatar_url || '/default-avatar.png'}
                        alt={req.name}
                        className="avatar-img"
                      />
                    </div>

                    {/* --- detail column --- */}
                    <div className="detail-column">
                      <div className="notification-message">
                        <strong>{req.name}</strong> 
                        {isOldDeclined ? (
                          <span> sent you a friend request</span>
                        ) : (
                          <span> sent you a friend request</span>
                        )}
                        {sameUserRequests.length > 1 && (
                          <span className="request-indicator">
                            ({sameUserRequests.filter(r => r.status === 'pending').length > 0 ? 'new request' : 'previous'})
                          </span>
                        )}
                      </div>

                      <div className="notification-time">{formatTimeAgo(req.created_at)}</div>

                      {/* Show buttons for pending requests that aren't being processed */}
                      {req.status === 'pending' && !processing.has(req.request_id) ? (
                        <div className="notification-actions">
                          <button
                            className="btn-accept"
                            onClick={() => respondToRequest(req.request_id, 'accepted')}
                          >
                            Accept
                          </button>
                          <button
                            className="btn-decline"
                            onClick={() => respondToRequest(req.request_id, 'declined')}
                          >
                            Decline
                          </button>
                        </div>
                      ) : req.status === 'pending' && processing.has(req.request_id) ? (
                        <div className="notification-processing">
                          <i className="fa-solid fa-spinner fa-spin" /> Processing…
                        </div>
                      ) : (
                        /* Show status for accepted/declined requests */
                        <div className="notification-status">
                          <span className={`status-tag ${req.status} ${isOldDeclined ? 'faded' : ''}`}>
                            {req.status === 'accepted' ? '✅ Accepted' : '❌ Declined'}
                          </span>
                          {req.responded_at && (
                            <div className="notification-time">
                              Responded {formatTimeAgo(req.responded_at)}
                            </div>
                          )}
                          {isOldDeclined && (
                            <div className="notification-info">
                              <small>Previous request - new request pending above</small>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              !loading && (
                <div className="notification-empty">
                  <i className="fa-regular fa-bell-slash" />
                  <p>No notifications yet</p>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;